import React, { useState, useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { Typography, Box, Container, Grid2, Skeleton} from "@mui/material";
import OurServices from "../../components/home-page-sections/our-services-section/services";
import Overlay from "../../assets/images/Overlay.png";
import OurTeam from "../../components/home-page-sections/our-team/team";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firestore-DB-config/firebase-config"; // Adjust the path
import { useLoader } from "../../components/loader/loader-context"; // Import the loader context

// Define the TypeScript interface to match your data structure
interface AboutData {
  aboutText: string;
  stats: {
    clients: number;
    projects: number;
    transactions: number;
    customers: number;
  };
}

function About() {
  // Initialize state with correct types
  const [aboutData, setAboutData] = useState<AboutData>({
    aboutText: "",
    stats: {
      clients: 0,
      projects: 0,
      transactions: 0,
      customers: 0,
    },
  });
  
  const { showLoader, hideLoader } = useLoader(); // Destructure the loader functions
  const [loading, setLoading] = useState(true); // Loading state


  // Fetch data from Firestore on component mount
  useEffect(() => {
    const fetchAboutData = async () => {
      showLoader();
      try {
        const docRef = doc(db, "aboutSection", "aboutData"); // Use your document ID
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          // Cast the document data to match AboutData type
          const data = docSnap.data() as AboutData;
          setAboutData(data);
        } else {
          console.error("No such document!");
        }
      } catch (error) {
        console.error("Error fetching about data: ", error);
      } finally {
        setLoading(false); // Set loading to false regardless of success or error
        hideLoader();
      }
    };

    fetchAboutData();
  }, []);

  const { aboutText, stats } = aboutData;

  if (loading) {
    return (
      <>
        <Header />
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ my: 6 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="80%" height={30} />
            <Skeleton variant="text" width="90%" height={30} />
            <Skeleton variant="text" width="90%" height={30} />
          </Box>
        ))}
        <Footer />
      </>
    );
  }

  return (
    <>
      {/* About us section */}
      <Header />
      <Container sx={{ my: { xs: 6, md: 8 }, px: { xs: 2, sm: 2, lg: 0 } }}>
        <Typography
          textAlign="left"
          sx={{ fontSize: { xs: "22px", sm: "32px" } }}
          fontWeight={605}
          mb={2}
          mt={8}
          color="rgba(1, 29, 51, 1)"
        >
          ABOUT US
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ fontSize: { xs: "14px", sm: "18px" } }}
            textAlign="left"
            color="textSecondary"
            lineHeight={1.5}
          >
            {/* Render the description using dangerouslySetInnerHTML to parse the HTML */}
            <span dangerouslySetInnerHTML={{ __html: aboutText }} />
          </Typography>
        </Box>
      </Container>

      <OurServices />

      {/* Overlay Section */}
      <Box
        sx={{
          my: { xs: 6, sm: 8, md: 10 }, // Adjust vertical margins for different screen sizes
          backgroundImage: `url(${Overlay})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: { xs: "250px", sm: "300px", md: "388px" }, // Responsive height
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Overlay for better contrast
          },
        }}
      >
        <Grid2
          container
          spacing={{ xs: 3, md: 8, lg: 26 }} // Responsive spacing between grid items
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: "1200px",
            mx: "auto",
          }} // Center content on larger screens
        >
          {[
            { number: stats.clients, label: "Happy Clients" },
            { number: stats.projects, label: "Completed Projects" },
            { number: stats.transactions, label: "Transactions" },
            { number: stats.customers, label: "Customers" },
          ].map((item, index) => (
            <Grid2
              key={index}
              size={{ xs: 6, md: 6, lg: 3 }}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                variant="h3" // Change variant for responsive typography scaling
                color="white"
                textAlign="center"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2.5rem", lg: "3.5rem" }, // Responsive font size
                }}
              >
                {item.number || 0} {/* Display the fetched number */}
              </Typography>
              <Typography
                variant="h6"
                color="white"
                textAlign="center"
                fontWeight="normal"
                sx={{
                  fontSize: { xs: "0.9rem", md: "1.10rem", lg: "1.25rem" }, // Responsive font size
                }}
              >
                {item.label}
              </Typography>
            </Grid2>
          ))}
        </Grid2>
      </Box>

      <OurTeam />
      <Footer />
    </>
  );
}

export default About;
